import { useEffect, useState } from 'react';
import { LevelOneApprover, ProgramOffice, ProgramOfficeStatus } from '../services/Types';
import { programOffice, updateProgramOffice as dbUpdateProgramOffice } from '../services/EventService';

// Global state management for program office data
let globalProgramOfficeState: ProgramOffice[] = [];
let globalProgramOfficeErrorState: string | null = null;
let globalProgramOfficeUpdatingState: boolean = false;
let globalProgramOfficeSubscribers: Set<(programOffices: ProgramOffice[], error: string | null, updating: boolean) => void> = new Set();

// Function to notify all subscribers of state changes
const notifySubscribers = () => {
  globalProgramOfficeSubscribers.forEach(callback => callback(globalProgramOfficeState, globalProgramOfficeErrorState, globalProgramOfficeUpdatingState));
};

// Function to update global state
const updateGlobalState = (programOffices: ProgramOffice[], error: string | null, updating: boolean) => {
  globalProgramOfficeState = programOffices;
  globalProgramOfficeErrorState = error;
  globalProgramOfficeUpdatingState = updating;
  notifySubscribers();
};

export const useProgramOfficeData = () => {
  const [programOfficeData, setProgramOfficeData] = useState<ProgramOffice[]>(globalProgramOfficeState);
  const [errorUseProgramOfficeData, setErrorUseProgramOfficeData] = useState<string | null>(globalProgramOfficeErrorState);
  const [updating, setUpdating] = useState<boolean>(globalProgramOfficeUpdatingState);
  const user = JSON.parse(localStorage.getItem('user') || '{}')

  useEffect(() => {
    // Subscribe to global state changes
    const handleStateChange = (newProgramOffices: ProgramOffice[], newError: string | null, newUpdating: boolean) => {
      setProgramOfficeData(newProgramOffices);
      setErrorUseProgramOfficeData(newError);
      setUpdating(newUpdating);
    };

    globalProgramOfficeSubscribers.add(handleStateChange);

    // Load program office data if not already loaded
    if (globalProgramOfficeState.length === 0) {
      loadProgramOfficeData();
    }

    // Cleanup subscription on unmount
    return () => {
      globalProgramOfficeSubscribers.delete(handleStateChange);
    };
  }, []);

  const resetProgramOfficeCache = () => {
    updateGlobalState([], null, false);
    setTimeout(() => {
      loadProgramOfficeData();
    }, 500);
  }

  const loadProgramOfficeData = async () => {
    if (globalProgramOfficeState.length > 0) {
      return true;
    }
    if (globalProgramOfficeUpdatingState) {
      return true;
    }
    try {
      updateGlobalState(globalProgramOfficeState, globalProgramOfficeErrorState, true);
      const { data } = await programOffice();
      if (data?.programOffice?.data) {
        updateGlobalState(data.programOffice.data, null, false);
        return true;
      }
      updateGlobalState([], 'Error loading program office data. Please try again later.', false);
      return false;
    } catch (error) {
      console.error('Error loading program office data:', error);
      updateGlobalState([], 'Error loading program office data. Please try again later.', false);
      return false;
    }
  };

  const getProgramOfficeData = (id: number) => {
    return globalProgramOfficeState.find((office: ProgramOffice) => office.id === id);
  };

  // Used by AutocompleteProgramOffice
  const getProgramOfficeSelectionOptions = () => {
    // Return all options if the user is an admin or an approver level > 1
    if (user.role === 'ADMIN' || (user.role === 'APPROVER' && user.approverLevel > 1)) {
      return globalProgramOfficeState.map((office: ProgramOffice) => ({
        label: office.title,
        value: office.id
      }));
    }
    // otherwise, return the requester's program office
    return globalProgramOfficeState.filter((office: ProgramOffice) =>
      office.id === user.programOffice.id).map((office: ProgramOffice) => ({
        label: office.title,
        value: office.id
      }));
  }

  const getMyProgramOffices = () => {
    // Return all options if the user is an admin or an approver level > 1
    if (user.role === 'ADMIN' || (user.role === 'APPROVER' && user.approverLevel > 1)) {
      return globalProgramOfficeState;
    }
    // otherwise, return the requester's program office
    return globalProgramOfficeState.filter((office: ProgramOffice) =>
      office.id === user.programOffice.id);
  }

  const updateProgramOffice = async (id: number, title: string, acronym: string, status: ProgramOfficeStatus, levelOneApprover: LevelOneApprover[]) => {
    const response = await dbUpdateProgramOffice(id, title, acronym, status.toString(), levelOneApprover);
    if (response?.data?.updateProgramOffice?.statusCode === 200) {
      resetProgramOfficeCache();
      return true;
    } else {
      return false;
    }
  };

  const getLevelOneApprovers = (id: number) => {
    const approvers = globalProgramOfficeState.find((office: ProgramOffice) =>
      office.id === id)?.levelOneApprover || [];
    return approvers;
  };

  const findLevelOneApproverByEmail = (email: string) => {
    // Find the first level one approver with matching email across all program offices
    const approver = globalProgramOfficeState.flatMap((office: ProgramOffice) =>
      office.levelOneApprover).find((approver: LevelOneApprover) => approver.email === email);
    if (approver) return approver;
    return null;
  };

  return {
    errorUseProgramOfficeData,
    programOfficeData,
    programOfficeCache: globalProgramOfficeState,
    getProgramOfficeData,
    getProgramOfficeSelectionOptions,
    getMyProgramOffices,
    getLevelOneApprovers,
    findLevelOneApproverByEmail,
    updateProgramOffice,
    resetProgramOfficeCache,
    updating
  };
}; 